import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from "chart.js";
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  Typography,
  Row,
  Col,
  Spin,
  Statistic,
} from "antd";
import useRequest from "../hooks/useRequest";
import apiPath from "../constants/apiPath";
import dayjs from "dayjs";
import {
  CreditCardOutlined,
  DollarOutlined,
  FileTextOutlined,
  UserOutlined,
} from "@ant-design/icons";
const { Text } = Typography;
// Register Chart.js components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
);

const columns = [
  {
    title: "Transaction ID",
    dataIndex: "transaction_id",
    key: "transaction_id",
  },
  {
    title: "Date",
    dataIndex: "created_at",
    key: "created_at",
    render: (date) => dayjs(date).format("DD MMM YYYY"),
  },
  {
    title: "User",
    key: "user",
    render: (_, record) => (
      <Space direction="vertical" size={0}>
        <Text strong>{record.user.full_name}</Text>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {record.user.email}
        </Text>
      </Space>
    ),
  },
  {
    title: "Package",
    key: "package",
    render: (_, record) => (
      <Space direction="vertical" size={0}>
        <Text strong>{record.package.name}</Text>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {record.package.duration}
        </Text>
      </Space>
    ),
  },
  {
    title: "Amount",
    dataIndex: "amount_paid",
    key: "amount_paid",
    render: (amount) => `₹${parseFloat(amount).toFixed(2)}`,
  },
  {
    title: "Status",
    dataIndex: "payment_status",
    key: "payment_status",
    render: (status) => (
      <Tag color={status === "SUCCESS" ? "green" : "red"}>{status}</Tag>
    ),
  },
];

const Dashboard = () => {
  const navigate = useNavigate();
  const { request } = useRequest();
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = () => {
    request({
      url: `api/${apiPath.dashboard}`,
      method: "GET",
      onSuccess: (data) => {
        setDashboardData(data.data);
      },
      onError: (err) => {
        console.log("Error fetching dashboard data", err);
      },
    });
  };

  // Prepare data for subscription chart
  const generateColors = (count) => {
    const colors = []

    for (let i = 0; i < count; i++) {
      const r = Math.floor(128 + 127 * Math.sin(1 * i + 0))
      const g = Math.floor(128 + 127 * Math.sin(1 * i + 2))
      const b = Math.floor(128 + 127 * Math.sin(1 * i + 4))

      console.log("rgb", r, g, b)

      colors.push({
        background: `rgba(${r}, ${g}, ${b}, 0.8)`,
        border: `rgba(${r - 20}, ${g - 20}, ${b - 20}, 1)`
      })
    }
    return colors
  }

  const colors = dashboardData
    ? generateColors(dashboardData.subscriptions.length)
    : []

  const subscriptionChartData = dashboardData
    ? {
        labels: dashboardData.subscriptions.map(sub => sub.name),
        datasets: [
          {
            label: "Users",
            data: dashboardData.subscriptions.map(sub => sub.user_count),
            backgroundColor: colors.map(c => c.background),
            borderColor: colors.map(c => c.border),
            borderWidth: 1,
          },
        ],
      }
    : null

  // const subscriptionChartData = dashboardData
  //   ? {
  //       labels: dashboardData.subscriptions.map((sub) => sub.name),
  //       datasets: [
  //         {
  //           label: "Users",
  //           data: dashboardData.subscriptions.map((sub) => sub.user_count),
  //           backgroundColor: [
  //             "rgba(255, 99, 132, 0.8)",
  //             "rgba(54, 162, 235, 0.8)",
  //             "rgba(255, 206, 86, 0.8)",
  //           ],
  //           borderColor: [
  //             "rgba(255, 99, 132, 1)",
  //             "rgba(54, 162, 235, 1)",
  //             "rgba(255, 206, 86, 1)",
  //           ],
  //           borderWidth: 1,
  //         },
  //       ],
  //     }
  //   : null;

  // Prepare data for revenue chart
  const revenueChartData = dashboardData
    ? {
        labels: dashboardData.subscriptions.map((sub) => sub.name),
        datasets: [
          {
            label: "Revenue (₹)",
            data: dashboardData.subscriptions.map((sub) => sub.revenue),
            backgroundColor: colors.map(c => c.background),
            borderColor: colors.map(c => c.border),
            borderWidth: 1,
          },
        ],
      }
    : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "65%", // inner hole size
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Subscription Distribution",
      },
    },
  };

  const StatCard = ({ title, value, icon, onClick, color = "primary" }) => (
    <div
      className={`card stat-card cursor-pointer border-${color}`}
      onClick={onClick}
      style={{ minHeight: "150px" }}
    >
      <div className="card-body d-flex flex-column justify-content-center align-items-center">
        <div className="stat-icon mb-3">
          <span className={`fs-1 text-${color}`}>{icon}</span>
        </div>
        <h5 className="card-title text-center">{title}</h5>
        <h2 className="text-center fw-bold">{value}</h2>
      </div>
    </div>
  );

  return (
    <div className="container-fluid">
      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {/* Total Users */}
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable onClick={() => navigate("/user-manager")}>
            <Statistic
              title="Total Users"
              value={dashboardData?.metrics?.total_users || 0}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>

        {/* Total Revenue */}
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable onClick={() => navigate("/transaction-manager")}>
            <Statistic
              title="Total Revenue"
              value={dashboardData?.metrics?.total_revenue || 0}
              prefix={<DollarOutlined />}
              formatter={(value) => `₹${Number(value).toLocaleString()}`}
            />
          </Card>
        </Col>

        {/* Documents Exchanged */}
        <Col xs={24} sm={12} lg={6}>
          <Card
            hoverable
            onClick={() => navigate("/report-manager")}
          >
            <Statistic
              title="Documents Exchanged"
              value={dashboardData?.metrics?.total_documents || 0}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>

        {/* Recent Transactions */}
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable onClick={() => navigate("/transaction-manager")}>
            <Statistic
              title="Recent Transactions"
              value={dashboardData?.subscription_transactions?.length || 0}
              prefix={<CreditCardOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Charts Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {/* Users by Subscription Chart */}
        <Col xs={24} md={12}>
          <Card
            title="Users by Subscription Type"
            bordered={false}
            style={{ height: "100%" }}
          >
            {subscriptionChartData ? (
              <div style={{ height: 350 }}>
                <Doughnut data={subscriptionChartData} options={chartOptions} />
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "60px 0" }}>
                <Spin />
              </div>
            )}
          </Card>
        </Col>

        {/* Revenue by Subscription Chart */}
        <Col xs={24} md={12}>
          <Card
            title="Revenue by Subscription Type"
            bordered={false}
            style={{ height: "100%" }}
          >
            {revenueChartData ? (
              <div style={{ height: 350 }}>
                <Doughnut data={revenueChartData} options={chartOptions} />
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "60px 0" }}>
                <Spin />
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Recent Transactions Table */}
      <Card
        title="Last 10 Subscription Transactions"
        extra={
          <Button
            type="primary"
            size="small"
            onClick={() => navigate("/transaction-manager")}
          >
            View All
          </Button>
        }
      >
        {dashboardData?.subscription_transactions?.length > 0 ? (
          <Table
            columns={columns}
            dataSource={dashboardData.subscription_transactions.slice(0, 10)}
            rowKey="id"
            pagination={false}
            scroll={{ x: true }}
          />
        ) : (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Text type="secondary">No transactions found</Text>
          </div>
        )}
      </Card>
    </div>
  );
};

export default Dashboard;
